import React, { useState, useEffect } from 'react';
import { Button, Input, message, Space, Spin } from 'antd';
import axios from 'axios';
import { format } from 'date-fns';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal ';
import '../pages/ApiKeys.css';
import { useParams } from 'react-router-dom';

const OrgApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [providers, setProviders] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState(null);
  const [initialFormData, setInitialFormData] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const { orgId } = useParams();

  useEffect(() => {
    fetchApiKeys();
    fetchProviders();
    fetchOrganizations();
  }, [pagination.current]);

  useEffect(() => {
    fetchApiKeys();
  }, [searchTerm]);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/orgApiKey/organization/${orgId}`, {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: searchTerm.trim(),
        },
      });

      const { data, meta } = response.data;
      const formattedApiKeys = data.map(apiKey => ({
        orgApiKeyId: apiKey.orgApiKeyId,
        id: apiKey.id,
        name: apiKey.name,
        key: apiKey.key,
        subKey: apiKey.subKey || 'N/A',
        region: apiKey.region || 'N/A',
        providerName: apiKey.providerName,
        isActive: apiKey.isActive ? 'ACTIVE' : 'INACTIVE',
        createdAt: format(new Date(apiKey.createdAt), 'MM/dd/yyyy, HH:mm:ss'),
        updatedAt: format(new Date(apiKey.updatedAt), 'MM/dd/yyyy, HH:mm:ss'),
      }));

      setApiKeys(formattedApiKeys);
      setPagination(prev => ({
        ...prev,
        total: meta.total,
      }));
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // If no data found (404), set the data to an empty array
        setApiKeys([]);
        setPagination(prev => ({ ...prev, total: 0 }));
        message.info('No data found'); // Inform the user
      } else {
        // Handle other errors
        console.error('Error fetching API keys:', error);
        message.error('Failed to load API keys');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/providers`);
      setProviders(response.data.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      message.error('Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/organizations`);
      setOrgs(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      message.error('Failed to load organizations');
    }
  };

  const openModal = (apiKey = null) => {
    setCurrentApiKey(apiKey);
    setInitialFormData(apiKey || {});
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setCurrentApiKey(null);
    setInitialFormData({});
  };

  const handleCreateUpdateApiKey = async formData => {
    try {
      const url = `${process.env.REACT_APP_API}/api/apikeys/create-org-api-key/${orgId}`;
      const dataToSend = {
        name: formData.name,
        key: formData.key,
        subKey: formData.subKey || null,
        region: formData.region || null,
        providerId: formData.providerId || null,
        isActive: formData.isActive === 'True' || formData.isActive === true,
      };

      if (currentApiKey) {
        await axios.patch(`${url}/${currentApiKey.id}`, dataToSend);
        message.success('API key updated successfully');
      } else {
        await axios.post(url, dataToSend);
        message.success('API key created successfully');
      }

      fetchApiKeys();
      closeModal();
    } catch (error) {
      console.error('Error creating/updating API key:', error);
      message.error('Failed to save API key');
    }
  };

  const handleToggleApiKeyStatus = async apiKey => {
    try {
      await axios.patch(`${process.env.REACT_APP_API}/api/apikeys/${apiKey.orgApiKeyId}/org-activate`, {});
      message.success('API key status updated successfully');
      fetchApiKeys();
    } catch (error) {
      console.error('Error updating API key status:', error);
      message.error('Failed to update API key status');
    }
  };

  const handleSearch = term => {
    setSearchTerm(term);
  };

  const handleTableChange = page => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  return (
    <div>
      <div className="apikeystab-page">
        <Space style={{ display: 'flex', justifyContent: 'end' }}>
          <Button type="primary" onClick={() => openModal()}>
            Create API Key
          </Button>
        </Space>
        <GenericTable
          data={apiKeys}
          searchKeys={['name', 'providerName', 'key']}
          sortableColumns={['name', 'providerName', 'createdAt']}
          actions={['Toggle Status']}
          actionFunctions={[
            apiKey => handleToggleApiKeyStatus(apiKey),
          ]}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handleTableChange,
          }}
          onSearch={handleSearch}
          toggle="isActive"
        />

        <GenericModal
          isVisible={isModalVisible}
          handleOk={handleCreateUpdateApiKey}
          handleCancel={closeModal}
          title={currentApiKey ? 'Edit API Key' : 'Create API Key'}
          inputs={[
            {
              label: 'Name',
              name: 'name',
              type: 'text',
              placeholder: 'Enter API key name',
              require: true,
            },
            {
              label: 'Key',
              name: 'key',
              type: 'text',
              placeholder: 'Enter API key',
              require: true,
            },
            {
              label: 'Sub Key',
              name: 'subKey',
              type: 'text',
              placeholder: 'Enter sub-key (optional)',
            },
            {
              label: 'Region',
              name: 'region',
              type: 'text',
              placeholder: 'Enter region (optional)',
            },
            {
              label: 'Provider',
              name: 'providerId',
              type: 'select',
              options: providers.map(provider => ({
                value: provider.id,
                label: provider.name,
              })),
              placeholder: 'Select provider',
              loading: providersLoading,
            },
          ]}
          actions={['Submit', 'Cancel']}
          actionFunctions={[handleCreateUpdateApiKey, closeModal]}
          predefinedData={initialFormData}
        />
      </div>
    </div>
  );
};

export default OrgApiKeys;
